import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { colors } from '../styles/GlobalStyles';
import { Button } from '../components/Button';
import { classService } from '../services/classService';
import { Loader } from '../components/Loader';

const Container = styled.div`
  min-height: 100vh;
  background: ${colors.background};
  padding: 20px;
`;

const Header = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px ${colors.shadow};
  margin-bottom: 30px;
  text-align: center;
`;

const Title = styled.h1`
  color: ${colors.primary};
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  color: ${colors.textLight};
  margin-bottom: 20px;
`;

const BackButton = styled(Button)`
  margin-bottom: 20px;
`;

const CalendarContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px ${colors.shadow};

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileListContainer = styled.div`
  display: none;
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px ${colors.shadow};

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 10px;
  flex-wrap: wrap;
`;

const MobileClassCard = styled.div`
  background: ${colors.background};
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  border-left: 4px solid ${colors.primary};
`;

const MobileClassTitle = styled.h3`
  color: ${colors.primary};
  margin: 0 0 8px 0;
  font-size: 16px;
`;

const MobileClassInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 10px;
`;

const MobileClassLabel = styled.span`
  font-size: 12px;
  color: ${colors.textLight};
  font-weight: 500;
`;

const MobileClassValue = styled.span`
  font-size: 14px;
  color: ${colors.text};
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const NavigationButton = styled.button`
  background: ${colors.primary};
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s ease;

  &:hover {
    background: ${colors.primaryDark};
  }
`;

const MonthTitle = styled.h2`
  color: ${colors.primary};
  margin: 0;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: ${colors.border};
  border-radius: 8px;
  overflow: hidden;
`;

const DayHeader = styled.div`
  background: ${colors.primary};
  color: white;
  padding: 15px 10px;
  text-align: center;
  font-weight: 600;
  font-size: 14px;
`;

const CalendarDay = styled.div.attrs(props => ({
  isOtherMonth: undefined,
  isToday: undefined,
}))`
  background: white;
  min-height: 120px;
  padding: 10px;
  border: 1px solid ${colors.border};
  position: relative;

  ${props => props.$isOtherMonth && `
    background: ${colors.background};
    color: ${colors.textLight};
  `}

  ${props => props.$isToday && `
    background: ${colors.secondary};
    color: white;
  `}
`;

const DayNumber = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 14px;
`;

const ClassList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ClassItem = styled.div.attrs(props => ({
  isRecurring: undefined,
}))`
  background: ${colors.primary};
  color: white;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: ${colors.primaryDark};
  }

  ${props => props.$isRecurring && `
    background: ${colors.success};
  `}
`;

const ClassTitle = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
`;

const ClassTime = styled.div`
  font-size: 10px;
  opacity: 0.9;
`;

const DetailModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  color: ${colors.primary};
  margin-bottom: 20px;
  text-align: center;
`;

const DetailRow = styled.div`
  margin-bottom: 15px;
  font-size: 14px;
  color: ${colors.text};
`;

const LocationLink = styled.a`
  color: ${colors.primary};
  text-decoration: none;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: ${colors.primaryDark};
    text-decoration: underline;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${colors.textLight};
`;

export const PublicSchedule = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState(null);
  const navigate = useNavigate();

  const weekDays = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

  // Get start of week (Monday for consistency)
  const getStartOfWeek = (date) => {
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day; // If Sunday, go back 6 days, else go to Monday
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() + diff);
    startOfWeek.setHours(0, 0, 0, 0);
    return startOfWeek;
  };

  // Generate calendar days for the week
  const generateCalendarDays = () => {
    const startOfWeek = getStartOfWeek(currentDate);
    const days = [];

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }

    return days;
  };

  // Get classes for a specific date
  const getClassesForDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    const filteredClasses = classes.filter(classItem => {
      if (classItem.isRecurring) {
        const classDate = new Date(classItem.date + 'T00:00:00');
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        
        return classDate.getDay() === date.getDay() && checkDate >= classDate;
      } else {
        return classItem.date === dateStr;
      }
    });
    
    // Sort by start time (startTime or time field)
    return filteredClasses.sort((a, b) => {
      const timeA = a.startTime || a.time || '00:00';
      const timeB = b.startTime || b.time || '00:00';
      return timeA.localeCompare(timeB);
    });
  };

  // Navigation functions
  const goToPreviousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get week range for display (Monday to Sunday for consistency)
  const getWeekRange = () => {
    const startOfWeek = getStartOfWeek(currentDate); // Always Monday for display
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    const startDay = startOfWeek.getDate();
    const endDay = endOfWeek.getDate();
    const month = startOfWeek.toLocaleDateString('tr-TR', { month: 'long' });
    const year = startOfWeek.getFullYear();

    if (startOfWeek.getMonth() === endOfWeek.getMonth()) {
      return `${startDay} - ${endDay} ${month} ${year}`;
    } else {
      const startMonth = startOfWeek.toLocaleDateString('tr-TR', { month: 'long' });
      const endMonth = endOfWeek.toLocaleDateString('tr-TR', { month: 'long' });
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
    }
  };

  // Get daily classes for mobile list view
  const getDailyClasses = () => {
    const startOfWeek = getStartOfWeek(currentDate); // Always Monday for consistency
    const dailyClasses = [];

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      const dayClasses = getClassesForDate(day);
      
      // Always show the day, even if no classes
      dailyClasses.push({
        date: day.toISOString().split('T')[0],
        displayDate: day.toLocaleDateString('tr-TR', { 
          day: 'numeric', 
          month: 'long', 
          year: 'numeric', 
          weekday: 'long' 
        }),
        classes: dayClasses
      });
    }

    return dailyClasses;
  };

  const fetchClasses = async () => {
    try {
      const data = await classService.getAllClasses();
      setClasses(data.filter(cls => cls.isActive));
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  const handleClassClick = (classItem) => {
    setSelectedClass(classItem);
  };

  const handleCloseModal = () => {
    setSelectedClass(null);
  };

  if (loading) {
    return <Loader fullScreen />;
  }

  const calendarDays = generateCalendarDays();

  return (
    <Container>
      <Header>
        <BackButton variant="secondary" onClick={() => navigate('/')}>
          ← Ana Sayfaya Dön
        </BackButton>
        <Title>📅 Haftalık Ders Programı</Title>
        <Subtitle>
          Seed Yoga Studio ders programını inceleyin. Rezervasyon için giriş yapmanız gerekmektedir.
        </Subtitle>
      </Header>

      <CalendarContainer>
        <CalendarHeader>
          <NavigationButton onClick={goToPreviousWeek}>
            ← Önceki Hafta
          </NavigationButton>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
            <MonthTitle>{getWeekRange()}</MonthTitle>
            <NavigationButton onClick={goToToday} style={{ fontSize: '12px', padding: '4px 8px' }}>
              Bugün
            </NavigationButton>
          </div>
          <NavigationButton onClick={goToNextWeek}>
            Sonraki Hafta →
          </NavigationButton>
        </CalendarHeader>

        <CalendarGrid>
          {weekDays.map(day => (
            <DayHeader key={day}>{day}</DayHeader>
          ))}
          
          {calendarDays.map((date, index) => {
            const dayData = {
              day: date.getDate(),
              date: date,
              classes: getClassesForDate(date),
              isOtherMonth: false,
              isToday: date.toDateString() === new Date().toDateString()
            };

            return (
              <CalendarDay
                key={index}
                $isOtherMonth={dayData.isOtherMonth}
                $isToday={dayData.isToday}
              >
                <DayNumber>{dayData.day}</DayNumber>
                <ClassList>
                  {dayData.classes.map((classItem, classIndex) => (
                    <ClassItem
                      key={classIndex}
                      $isRecurring={classItem.isRecurring}
                      onClick={() => handleClassClick(classItem)}
                    >
                      <ClassTitle>{classItem.title}</ClassTitle>
                      <ClassTime>
                        {classItem.startTime}
                        {classItem.endTime ? ` - ${classItem.endTime}` : ''}
                      </ClassTime>
                    </ClassItem>
                  ))}
                </ClassList>
              </CalendarDay>
            );
          })}
        </CalendarGrid>
      </CalendarContainer>

      {/* Mobile List View */}
      <MobileListContainer>
        <MobileListHeader>
          <NavigationButton onClick={goToPreviousWeek} style={{ fontSize: '12px', padding: '8px 12px', minWidth: '80px' }}>
            ← Önceki
          </NavigationButton>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: '8px', 
            flex: '1',
            minWidth: '140px',
            maxWidth: '200px'
          }}>
            <MonthTitle style={{ fontSize: '16px', fontWeight: '600', textAlign: 'center', lineHeight: '1.2' }}>
              {getWeekRange()}
            </MonthTitle>
            <NavigationButton onClick={goToToday} style={{ fontSize: '12px', padding: '6px 12px', minWidth: '60px' }}>
              Bugün
            </NavigationButton>
          </div>
          <NavigationButton onClick={goToNextWeek} style={{ fontSize: '12px', padding: '8px 12px', minWidth: '80px' }}>
            Sonraki →
          </NavigationButton>
        </MobileListHeader>

        {getDailyClasses().map((dayData, dayIndex) => (
          <div key={dayData.date}>
            <h3 style={{ 
              margin: '20px 0 10px 0', 
              fontSize: '18px',
              textAlign: 'center',
              background: colors.primary,
              color: 'white',
              padding: '10px',
              borderRadius: '8px'
            }}>
              {dayData.displayDate}
            </h3>
            
            {dayData.classes.length > 0 ? (
              dayData.classes.map(classItem => (
                <MobileClassCard key={classItem.id} onClick={() => handleClassClick(classItem)}>
                  <MobileClassTitle>{classItem.title}</MobileClassTitle>
                  <MobileClassInfo>
                    <MobileClassLabel>Eğitmen:</MobileClassLabel>
                    <MobileClassValue>{classItem.instructor}</MobileClassValue>
                    
                    <MobileClassLabel>Saat:</MobileClassLabel>
                    <MobileClassValue>{classItem.time}</MobileClassValue>
                    
                    <MobileClassLabel>Adres:</MobileClassLabel>
                    <MobileClassValue>{classItem.location}</MobileClassValue>
                    
                    <MobileClassLabel>Tip:</MobileClassLabel>
                    <MobileClassValue>{classItem.isRecurring ? 'Tekrarlayan' : 'Tek Seferlik'}</MobileClassValue>
                  </MobileClassInfo>
                  
                  {classItem.description && (
                    <p style={{ 
                      fontSize: '12px', 
                      color: colors.textLight, 
                      margin: '8px 0 0 0',
                      fontStyle: 'italic'
                    }}>
                      {classItem.description}
                    </p>
                  )}
                </MobileClassCard>
              ))
            ) : (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: colors.textLight,
                fontSize: '14px',
                fontStyle: 'italic'
              }}>
                Bu gün için ders bulunmuyor
              </div>
            )}
          </div>
        ))}
      </MobileListContainer>

      {selectedClass && (
        <DetailModal onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={handleCloseModal}>×</CloseButton>
            
            <ModalTitle>{selectedClass.title}</ModalTitle>

            <DetailRow>
              <strong>👨‍🏫 Eğitmen:</strong> {selectedClass.instructor}
            </DetailRow>

            {selectedClass.description && (
              <DetailRow>
                <strong>📝 Açıklama:</strong> {selectedClass.description}
              </DetailRow>
            )}

            <DetailRow>
              <strong>📅 Tarih:</strong> {selectedClass.date}
              {selectedClass.isRecurring && ' (Tekrarlı)'}
            </DetailRow>

            <DetailRow>
              <strong>🕐 Saat:</strong> {selectedClass.startTime}
              {selectedClass.endTime && ` - ${selectedClass.endTime}`}
            </DetailRow>

            {selectedClass.isOnline ? (
              <DetailRow>
                <strong>💻 Online Ders</strong>
                {selectedClass.onlineLink && (
                  <>
                    {' - '}
                    <LocationLink 
                      href={selectedClass.onlineLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Derse Katıl
                    </LocationLink>
                  </>
                )}
              </DetailRow>
            ) : selectedClass.location && (
              <DetailRow>
                <strong>📍 Adres:</strong>{' '}
                <LocationLink 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedClass.location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {selectedClass.location}
                </LocationLink>
              </DetailRow>
            )}

            <DetailRow>
              <strong>👥 Kontenjan:</strong> {selectedClass.currentBookings} / {selectedClass.capacity} Dolu
            </DetailRow>

            <DetailRow>
              <strong>🪑 Boş Yer:</strong> {selectedClass.availableSpots}
            </DetailRow>

            <div style={{ marginTop: '30px', textAlign: 'center' }}>
              <Button onClick={() => navigate('/')} variant="primary">
                Rezervasyon için Giriş Yap
              </Button>
            </div>
          </ModalContent>
        </DetailModal>
      )}
    </Container>
  );
};
